import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { Skeleton } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import moment from "moment";
// import TransactionDetailsDialog from "./TransactionDetailsDialog";
import { toCurrency } from "utils/helpers";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Typography,
  Paper,
} from "@mui/material";
import { hi } from "date-fns/locale";

const serverId = process.env.REACT_APP_SERVER_ID;
const siteId = process.env.REACT_APP_SITE_ID;
const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const textHighlight = process.env.REACT_APP_TEXT_HIGHLIGHT;
const buttonColor = process.env.REACT_APP_BUTTON_COLOR;
const buttonText = process.env.REACT_APP_BUTTON_TEXT;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const headCells = [
  {
    id: "ChargeDate",
    label: "Date Ordered",
  },
  {
    id: "CheckNumber",
    label: "Check Number",
    align: "center",
  },
  {
    id: "Total",
    label: "Payment Amount",
    align: "center",
  },
  {
    id: "MediaNames",
    label: "Payment Type",
    align: "center",
  },
  // {
  //   id: "RunningBalance",
  //   label: "Running Balance",
  //   align: "center",
  // },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead id="transaction-tablehead">
      <TableRow
        sx={{
          backgroundColor: buttonColor,
          "& .MuiTableSortLabel.active": {
            color: buttonText,
          },
          "& .Mui-active": {
            color: "whitesmoke !important",
          },
          "& .Mui-active .MuiTableSortLabel-icon": {
            color: "whitesmoke !important",
          },
          "& .MuiButtonBase-root-MuiTableSortLabel-root:hover": {
            color: "whitesmoke !important",
          },
        }}
      >
        {headCells.map((headCell) => {
          return (
            <TableCell
              key={headCell.id}
              align={headCell?.align || "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{
                "@media (min-width: 271px) and (max-width: 587px)": {
                  fontSize: "1rem",
                  lineHeight: "normal",
                  padding: "8px 4px",
                  fontWeight: 600,
                },
              }}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "desc"}
                onClick={createSortHandler(headCell.id)}
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                    "& .MuiTableSortLabel-icon": {
                      fontSize: "1rem",
                    },
                  },
                }}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box
                    component="span"
                    sx={{
                      ...visuallyHidden,
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  >
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const TransactionsTable = ({ transactions }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [saleData, setSaleData] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [selectedCheckNumber, setSelectedCheckNumber] = useState(null);
  const [selectedChargeDate, setSelectedChargeDate] = useState(null);
  const [selectedSubtotal, setSelectedSubTotal] = useState(null);
  const [selectedTip, setSelectedTip] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [selectedServiceFee, setSelectedServiceFee] = useState(null);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [selectedTotal, setSelectedTotal] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  const handleClick = (
    SaleId,
    CheckNumber,
    ChargeDate,
    SubTotal,
    Tip,
    Tax,
    ServiceFee,
    Discount,
    Amount,
    Total,
    PaymentType
  ) => {
    setSelectedSaleId(SaleId);
    setSelectedCheckNumber(CheckNumber);
    setSelectedChargeDate(ChargeDate);
    setSelectedSubTotal(SubTotal);
    setSelectedTip(Tip);
    setSelectedTax(Tax);
    setSelectedServiceFee(ServiceFee);
    setSelectedDiscount(Discount);
    setSelectedTotal(Total || Amount);
    setSelectedPaymentType(PaymentType);
    setOpen(true);
  };

  const getDataForSaleId = async (selectedSaleId) => {
    try {
      const response = await fetch(
        `${apiBaseURL}/customer/sale-items/${serverId}/${siteId}/${selectedSaleId}`
      );
      const data = await response.json();
      // console.log(data);
      setSaleData(data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error retrieving data:", error);
    }
  };

  useEffect(() => {
    if (selectedSaleId) {
      getDataForSaleId(selectedSaleId);
    }
  }, [selectedSaleId]);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("ChargeDate");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const rows = useMemo(() => {
    return transactions.map(({ ServiceFee, ...others }) => {
      return {
        ...others,
        ServiceFee: parseFloat(ServiceFee),
      };
    });
  }, [transactions]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(() => {
    const sortedRows = [...rows].sort((a, b) => {
      if (orderBy === "ChargeDate") {
        const dateA = new Date(a.ChargeDate);
        const dateB = new Date(b.ChargeDate);
        return order === "asc" ? dateA - dateB : dateB - dateA;
      } else if (orderBy === "Amount") {
        return order === "asc" ? a.Amount - b.Amount : b.Amount - a.Amount;
      }
      // Add more sorting conditions for other properties if needed

      return 0; // default return for equal values
    });

    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [rows, page, rowsPerPage, order, orderBy]);

  return (
    <Box
      id="transaction-pagination"
      sx={{
        width: "100%",
        "@media (min-width: 271px) and (max-width: 587px)": {
          fontSize: "1rem",
          lineHeight: "normal",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          mb: 1,
          "@media (min-width: 271px) and (max-width: 587px)": {
            mb: 0.5,
          },
        }}
      >
        <Dialog
          maxWidth="md"
          fullWidth
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            "@media (min-width: 271px) and (max-width: 587px)": {
              "& .MuiDialog-paper": {
                margin: "8px",
                maxHeight: "calc(100vh - 16px)",
                fontSize: "1rem",
                lineHeight: "normal",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "rgba(211, 211, 211, 1)",
              fontWeight: 600,
              "@media (min-width: 271px) and (max-width: 587px)": {
                fontSize: "1rem",
                lineHeight: "normal",
                padding: "12px 16px",
                backgroundColor: "rgba(211, 211, 211, 1)",
              },
            }}
          >
            Transaction Details
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              "@media (min-width: 271px) and (max-width: 587px)": {
                padding: "12px 16px",
                fontSize: "1rem",
                lineHeight: "normal",
              },
            }}
          >
            {/* Check Number and Date */}
            <Box
              mb={1}
              px={1}
              py={1}
              borderRadius={0}
              bgcolor="rgba(211, 211, 211, 0.3)"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                "@media (min-width: 271px) and (max-width: 587px)": {
                  mb: 0.5,
                  px: 0.5,
                  py: 0.5,
                  borderRadius: 0,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                component="h7"
                sx={{
                  fontWeight: 600,
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Check #:{" "}
                {isLoading ? (
                  <Skeleton
                    width={80}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{selectedCheckNumber}</span>
                )}
              </Typography>
              <Typography
                variant="subtitle1"
                component="h7"
                sx={{
                  fontWeight: 600,
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Date:{" "}
                {isLoading ? (
                  <Skeleton
                    width={150}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>
                    {moment(selectedChargeDate).format("MMM. DD, YYYY")}
                  </span>
                )}
              </Typography>
            </Box>

            {/* Payment Type */}
            <Box
              mb={1}
              px={1}
              py={1}
              borderRadius={0}
              bgcolor="rgba(211, 211, 211, 0.3)"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                "@media (min-width: 271px) and (max-width: 587px)": {
                  mb: 0.5,
                  px: 0.5,
                  py: 0.5,
                  borderRadius: 0,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                component="h7"
                sx={{
                  fontWeight: 600,
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Payment Type:{" "}
                {isLoading ? (
                  <Skeleton
                    width={100}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{selectedPaymentType || "-"}</span>
                )}
              </Typography>
            </Box>

            {/* Sale Data Table */}
            <TableContainer
              component={Paper}
              sx={{
                marginBottom: "16px",
                "@media (min-width: 271px) and (max-width: 587px)": {
                  marginBottom: "8px",
                  fontSize: "1rem",
                  lineHeight: "normal",
                },
              }}
            >
              <Table
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        textAlign: "left",
                        lineHeight: "normal",
                        fontWeight: 600,
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                        },
                      }}
                    >
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        textAlign: "left",
                        lineHeight: "normal",
                        fontWeight: 600,
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                        },
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        textAlign: "right",
                        lineHeight: "normal",
                        fontWeight: 600,
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                        },
                      }}
                    >
                      Unit Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1rem",
                        textAlign: "right",
                        lineHeight: "normal",
                        fontWeight: 600,
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                        },
                      }}
                    >
                      Total Price
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saleData.map(
                    (item) =>
                      item.Quantity !== 0 &&
                      item.ReceiptDescription !== "" && (
                        <TableRow key={item.SaleItemId}>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              textAlign: "left",
                              lineHeight: "normal",
                              height: "2rem",
                              padding: "0 1rem",
                              "@media (min-width: 271px) and (max-width: 587px)":
                                {
                                  fontSize: "1rem",
                                  lineHeight: "normal",
                                },
                            }}
                          >
                            <span
                              style={{
                                marginLeft: "1.8rem",
                                "@media (min-width: 271px) and (max-width: 587px)":
                                  {
                                    marginLeft: "0.5rem",
                                  },
                              }}
                            >
                              {item.Quantity}
                            </span>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              textAlign: "left",
                              lineHeight: "normal",
                              height: "2rem",
                              padding: "0 1rem",
                              "@media (min-width: 271px) and (max-width: 587px)":
                                {
                                  fontSize: "1rem",
                                  lineHeight: "normal",
                                },
                            }}
                          >
                            {item.ReceiptDescription}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              textAlign: "right",
                              lineHeight: "normal",
                              height: "2rem",
                              padding: "0 1rem",
                              "@media (min-width: 271px) and (max-width: 587px)":
                                {
                                  fontSize: "1rem",
                                  lineHeight: "normal",
                                },
                            }}
                          >
                            {toCurrency(item.BasePrice / 100)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              textAlign: "right",
                              lineHeight: "normal",
                              height: "2rem",
                              padding: "0 1rem",
                              "@media (min-width: 271px) and (max-width: 587px)":
                                {
                                  fontSize: "1rem",
                                  lineHeight: "normal",
                                },
                            }}
                          >
                            {toCurrency((item.BasePrice / 100) * item.Quantity)}
                          </TableCell>
                        </TableRow>
                      )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Amounts */}
            <Box
              mt={2}
              px={2}
              py={1}
              borderRadius={4}
              sx={{
                "@media (min-width: 271px) and (max-width: 587px)": {
                  mt: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                component="h7"
                display="flex"
                justifyContent="space-between"
                sx={{
                  fontWeight: 600,
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Subtotal:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{toCurrency(selectedSubtotal || 0)}</span>
                )}
              </Typography>
              <Typography
                variant="subtitle1"
                component="h7"
                display="flex"
                justifyContent="space-between"
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Tip:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{toCurrency(selectedTip || 0)}</span>
                )}
              </Typography>
              <Typography
                variant="subtitle1"
                component="h7"
                display="flex"
                justifyContent="space-between"
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Tax:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{toCurrency(selectedTax || 0)}</span>
                )}
              </Typography>
              <Typography
                variant="subtitle1"
                component="h7"
                display="flex"
                justifyContent="space-between"
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Service Fee:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{toCurrency(selectedServiceFee || 0)}</span>
                )}
              </Typography>
              <Typography
                variant="subtitle1"
                component="h7"
                display="flex"
                justifyContent="space-between"
                sx={{
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                  },
                }}
              >
                Discounts:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <span>{toCurrency(selectedDiscount || 0)}</span>
                )}
              </Typography>
              <Typography
                variant="h6"
                component="h7"
                display="flex"
                justifyContent="space-between"
                mt={1}
                sx={{
                  fontWeight: 600,
                  "@media (min-width: 271px) and (max-width: 587px)": {
                    fontSize: "1rem",
                    lineHeight: "normal",
                    mt: 0.5,
                  },
                }}
              >
                Total:{" "}
                {isLoading ? (
                  <Skeleton
                    width={60}
                    sx={{
                      "@media (min-width: 271px) and (max-width: 587px)": {
                        fontSize: "1rem",
                        lineHeight: "normal",
                      },
                    }}
                  />
                ) : (
                  <strong>{toCurrency(selectedTotal || 0)}</strong>
                )}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              "@media (min-width: 271px) and (max-width: 587px)": {
                padding: "8px 16px",
                fontSize: "1rem",
                lineHeight: "normal",
              },
            }}
          >
            <Button
              onClick={() => setOpen(false)}
              sx={{
                backgroundColor: buttonColor,
                color: buttonText,
                textTransform: "none",
                marginRight: "1rem !important",
                "&:hover": { backgroundColor: textHighlight },
                "@media (min-width: 271px) and (max-width: 587px)": {
                  fontSize: "1rem",
                  lineHeight: "normal",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <TableContainer
          sx={{
            "@media (min-width: 271px) and (max-width: 587px)": {
              overflowX: "auto",
              fontSize: "1rem",
              lineHeight: "normal",
            },
          }}
        >
          <Table
            sx={{
              minWidth: 750,
              background: "white",
              "@media (min-width: 271px) and (max-width: 587px)": {
                minWidth: "100%",
                fontSize: "1rem",
                lineHeight: "normal",
              },
            }}
            aria-labelledby="tableTitle"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.name);
                const labelId = `enhanced-table-checkbox-${index}`;
                // console.log(row);

                return (
                  <StyledTableRow
                    hover
                    // onClick={(event) => handleClick(event, row.name)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={index}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      handleClick(
                        row.SaleId,
                        row.CheckNumber,
                        row.ChargeDate,
                        row.SubTotal,
                        row.Tip,
                        row.Tax,
                        row.ServiceFee,
                        row.Discount,
                        row.Amount,
                        row.Total,
                        row.MediaNames || row.PaymentMethod || "-"
                      )
                    }
                  >
                    <TableCell>
                      <Button
                        sx={{
                          backgroundColor: buttonColor,
                          textTransform: "none",
                          boxShadow: "none",
                          borderRadius: "1rem !important",
                          fontSize: "0.875rem",
                          padding: "6px 16px !important",
                          ":hover": {
                            backgroundColor: textHighlight,
                          },
                          "@media (min-width: 271px) and (max-width: 587px)": {
                            fontSize: "1rem",
                            lineHeight: "normal",
                            padding: "8px 4px",
                          },
                        }}
                        disableRipple
                      >
                        {moment(row?.ChargeDate).format("MMM. DD, YYYY")}
                      </Button>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                          padding: "8px 4px",
                        },
                      }}
                    >
                      {row?.CheckNumber || 0}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                          padding: "8px 4px",
                        },
                      }}
                    >
                      {toCurrency(row?.Amount || 0)}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                          padding: "8px 4px",
                        },
                      }}
                    >
                      {row?.MediaNames || row?.PaymentMethod || "-"}
                    </TableCell>

                    {/* <TableCell
                      align="center"
                      sx={{
                        "@media (min-width: 271px) and (max-width: 587px)": {
                          fontSize: "1rem",
                          lineHeight: "normal",
                          padding: "8px 4px",
                        },
                      }}
                    >
                      {toCurrency(row?.RunningBalance || 0)}
                    </TableCell> */}
                  </StyledTableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[15, 30, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "@media (min-width: 271px) and (max-width: 587px)": {
              fontSize: "1rem !important",
              lineHeight: "normal !important",
              "& .MuiTablePagination-toolbar": {
                fontSize: "1rem !important",
                lineHeight: "normal !important",
              },
              "& .MuiTablePagination-selectLabel": {
                fontSize: "1rem !important",
                lineHeight: "normal !important",
                marginBottom: "0 !important",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "1rem !important",
                lineHeight: "normal !important",
              },
              "& .MuiSelect-select": {
                fontSize: "1rem !important",
                lineHeight: "normal !important",
              },
              "& .MuiIconButton-root": {
                fontSize: "1rem !important",
              },
            },
            background: "white",
            mt: 1,
          }}
        />
      </Box>
    </Box>
  );
};
export default TransactionsTable;
